import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Equipment } from '../../types/equipment';
import { deleteEquipment } from '../../features/equipment/equipmentSlice';
import EditEquipmentForm from './EditEquipmentForm';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { formatDate } from '../../utils/formatters';
import { Tool, AlertTriangle } from 'react-feather';

interface Props {
  equipment: Equipment;
}

const EquipmentCard: React.FC<Props> = ({ equipment }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  const handleDelete = async () => {
    if (window.confirm(t('common.areYouSure'))) {
      await dispatch(deleteEquipment(equipment.id));
    }
  };

  const getStatusColor = (status: Equipment['status']) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800';
      case 'retired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const isMaintenanceDue = equipment.maintenance_due && 
    new Date(equipment.maintenance_due) <= new Date();

  if (isEditing) {
    return (
      <EditEquipmentForm
        equipment={equipment}
        onCancel={() => setIsEditing(false)}
        onSuccess={() => setIsEditing(false)}
      />
    );
  }

  return (
    <Card>
      <div className="space-y-4">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{equipment.name}</h3>
            <p className="text-sm text-gray-600">{equipment.type}</p>
          </div>
          <div className="flex items-center gap-2">
            {isMaintenanceDue && (
              <AlertTriangle 
                className="text-yellow-500" 
                title={t('maintenanceOverdue')}
              />
            )}
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(equipment.status)}`}>
              {t(`equipment.status.${equipment.status}`)}
            </span>
          </div>
        </div>

        <div className="space-y-2 text-sm text-gray-600">
          {equipment.purchase_date && (
            <p>{t('purchaseDate')}: {formatDate(equipment.purchase_date)}</p>
          )}
          {equipment.maintenance_due && (
            <p>{t('maintenanceDue')}: {formatDate(equipment.maintenance_due)}</p>
          )}
          {equipment.notes && (
            <p className="italic">{equipment.notes}</p>
          )}
        </div>

        <div className="flex justify-end space-x-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setIsEditing(true)}
          >
            {t('common.edit')}
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={handleDelete}
          >
            {t('common.delete')}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default EquipmentCard;